import { useState } from 'react';

import { useI18n } from 'modules/i18n';
import PropTypes from 'prop-types';

import { useTouchFieldOnBlur } from '../../hooks';
import Element from '../shared/Element';

export default function SelectField({
    scope,
    attribute,
    value,
    values,
    keepOrder = false,
    data,
    validate,
    label,
    labelKey,
    showErrors,
    handleChange,
    handlechangecallback,
    handleErrors,
    touchField,
    help,
    individualErrorMsg,
    hidden,
    className,
    doNotTranslate = false,
    optionsScope,
    withEmpty = false,
    id,
}) {
    const onBlur = useTouchFieldOnBlur(touchField);

    const defaultValue = value || data?.[attribute];
    const { t, locale } = useI18n();

    // Track current value for validation display
    const [currentValue, setCurrentValue] = useState(defaultValue);

    const onChange = (event) => {
        const newValue = event.target.value;
        const name = event.target.name;

        setCurrentValue(newValue);

        handleChange(name, newValue, data);

        if (typeof handlechangecallback === 'function') {
            handlechangecallback(name, newValue);
        }

        if (typeof validate === 'function') {
            const valid = validate(newValue);
            handleErrors(name, !valid);
        }
    };

    const selectTextAndValueFunction = (value) => {
        if (typeof value === 'string') {
            let translationPrefix = optionsScope || `${scope}.${attribute}`;
            return function (value) {
                return {
                    text: doNotTranslate
                        ? value
                        : t(`${translationPrefix}.${value}`),
                    value: value,
                };
            };
        } else {
            return function (value) {
                return {
                    text:
                        (value.name && value.name[locale]) ||
                        (typeof value.name === 'string' && value.name),
                    value: value.value || value.id,
                };
            };
        }
    };

    const options = () => {
        let opts = [];
        let rawOpts;

        if (values) {
            if (Array.isArray(values)) {
                rawOpts = values;
            } else {
                rawOpts = Object.keys(values).map((id) => {
                    return { id: id, name: values[id].name };
                });
            }
        } else if (data && attribute === 'workflow_state') {
            rawOpts = data.workflow_states;
        }

        if (rawOpts) {
            let getTextAndValue = selectTextAndValueFunction(rawOpts[0]);
            if (!keepOrder) {
                rawOpts.sort((a, b) => {
                    let textA = getTextAndValue(a).text;
                    let textB = getTextAndValue(b).text;
                    return new Intl.Collator(locale).compare(textA, textB);
                });
            }

            opts = rawOpts.map((value, index) => {
                if (value) {
                    let textAndValue = getTextAndValue(value);
                    return (
                        <option
                            value={textAndValue.value}
                            key={`${scope}-${index}`}
                        >
                            {textAndValue.text}
                        </option>
                    );
                }
            });
        }

        if (withEmpty) {
            opts.unshift(
                <option value="" key={`${scope}-choose`}>
                    {t('choose')}
                </option>
            );
        }
        return opts;
    };

    return (
        <Element
            scope={scope}
            attribute={attribute}
            label={label}
            labelKey={labelKey}
            htmlFor={`${scope}_${id ? id : attribute}`}
            showErrors={showErrors}
            className={className}
            hidden={hidden}
            valid={
                typeof validate === 'function' ? validate(currentValue) : true
            }
            mandatory={typeof validate === 'function'}
            elementType="select"
            individualErrorMsg={individualErrorMsg}
            help={help}
        >
            <select
                id={`${scope}_${id ? id : attribute}`}
                name={attribute}
                className="Input"
                defaultValue={defaultValue}
                onChange={onChange}
                onBlur={onBlur}
                data-testid={`${scope}-${attribute}-select`}
            >
                {options()}
            </select>
        </Element>
    );
}

SelectField.propTypes = {
    scope: PropTypes.string,
    attribute: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object,
    ]),
    values: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    keepOrder: PropTypes.bool,
    data: PropTypes.object,
    validate: PropTypes.func,
    label: PropTypes.string,
    labelKey: PropTypes.string,
    showErrors: PropTypes.bool,
    handleChange: PropTypes.func,
    handlechangecallback: PropTypes.func,
    handleErrors: PropTypes.func,
    touchField: PropTypes.func,
    help: PropTypes.string,
    individualErrorMsg: PropTypes.string,
    hidden: PropTypes.bool,
    className: PropTypes.string,
    doNotTranslate: PropTypes.bool,
    optionsScope: PropTypes.string,
    withEmpty: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    elementType: PropTypes.string,
    condition: PropTypes.bool,
    accept: PropTypes.string,
};
